/**
 * Walks a Zod object schema and emits a flat list of editable field
 * descriptors so registry_spec (and any property panel) can describe the
 * right input per prop.
 *
 * Why not `instanceof ZodString` etc.: pnpm gives @dynamic-landing/components
 * its own copy of zod. instanceof checks across copies always fail. We use the
 * `_def.typeName` discriminator instead, which is part of Zod's stable internal
 * API and survives across copies.
 *
 * Why not `import type { ZodTypeAny } from "zod"`: studio-ui does not take zod
 * as a direct dependency. The component registry's `schema` field is already
 * typed as a Zod schema by @dynamic-landing/components; we accept it via a
 * structural `SchemaLike` type so this module needs no zod import.
 *
 * Supported field kinds: string, number, boolean, enum, json (anything else
 * falls back to a JSON textarea).
 */

/** Minimal structural view of a Zod schema — just what introspection needs. */
export type SchemaLike = {
  _def: ZodDef;
  safeParse: (data: unknown) => SafeParseResult;
};

export type SafeParseResult =
  | { success: true; data: unknown }
  | {
      success: false;
      error: {
        message: string;
        issues: Array<{
          code: string;
          received?: unknown;
          path: (string | number)[];
        }>;
      };
    };

export type FieldKind =
  | { kind: "string"; multiline: boolean }
  | { kind: "number" }
  | { kind: "boolean" }
  | { kind: "enum"; options: string[] }
  | { kind: "json" };

export type FieldDescriptor = {
  name: string;
  required: boolean;
  defaultValue: unknown;
  kind: FieldKind;
};

type ZodDef = { typeName: string; [k: string]: unknown };

type ZodSchemaLike = { _def: ZodDef };

function defOf(s: ZodSchemaLike): ZodDef {
  return s._def;
}

export function describeSchema(schema: SchemaLike): FieldDescriptor[] {
  const obj = unwrapToObject(schema);
  if (!obj) return [];

  // ZodObject's shape may be a function (for lazy schemas) or an object.
  const shapeRaw = defOf(obj).shape as unknown;
  const shape =
    typeof shapeRaw === "function"
      ? (shapeRaw as () => Record<string, ZodSchemaLike>)()
      : (shapeRaw as Record<string, ZodSchemaLike>);

  const out: FieldDescriptor[] = [];
  for (const [name, raw] of Object.entries(shape)) {
    const descriptor = describeField(name, raw);
    if (descriptor) out.push(descriptor);
  }
  return out;
}

function describeField(name: string, schema: ZodSchemaLike): FieldDescriptor | null {
  let cur: ZodSchemaLike = schema;
  let required = true;
  let defaultValue: unknown = undefined;

  // Peel ZodOptional / ZodNullable / ZodDefault.
  while (true) {
    const def = defOf(cur);
    if (def.typeName === "ZodOptional" || def.typeName === "ZodNullable") {
      required = false;
      cur = def.innerType as ZodSchemaLike;
      continue;
    }
    if (def.typeName === "ZodDefault") {
      required = false;
      const dv = def.defaultValue as () => unknown;
      try {
        defaultValue = dv();
      } catch {
        /* ignore */
      }
      cur = def.innerType as ZodSchemaLike;
      continue;
    }
    break;
  }

  const def = defOf(cur);
  let kind: FieldKind;
  switch (def.typeName) {
    case "ZodString": {
      const multiline = /(content|description|legal|message|body|notes|subline)/i.test(name);
      kind = { kind: "string", multiline };
      break;
    }
    case "ZodNumber":
      kind = { kind: "number" };
      break;
    case "ZodBoolean":
      kind = { kind: "boolean" };
      break;
    case "ZodEnum": {
      const opts = (def.values as readonly string[] | undefined) ?? [];
      kind = { kind: "enum", options: [...opts] };
      break;
    }
    default:
      kind = { kind: "json" };
  }

  return { name, required, defaultValue, kind };
}

function unwrapToObject(schema: ZodSchemaLike): ZodSchemaLike | null {
  let cur: ZodSchemaLike = schema;
  while (true) {
    const def = defOf(cur);
    if (def.typeName === "ZodObject") return cur;
    if (def.typeName === "ZodOptional" || def.typeName === "ZodNullable") {
      cur = def.innerType as ZodSchemaLike;
      continue;
    }
    if (def.typeName === "ZodDefault") {
      cur = def.innerType as ZodSchemaLike;
      continue;
    }
    return null;
  }
}
